import type { RingSpec } from './types';

// Guarded so the module also loads under plain Node (headless tests via tsx).
export const DEV = Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);

/**
 * Chime tunings. Eight tubes, low-voiced so the inharmonic partials of a struck tube
 * (1 : 2.76 : 5.40 : 8.93 ...) fill the cathedral rather than shriek in it.
 * Default is Scriabin's mystic chord (C F# Bb E A D) plus a b9 (Db) and a #5 (G#):
 * discordant, and still lovely when it rings out slowly under reverb.
 */
export interface Tuning { name: string; label: string; hz: number[] }

const C3 = 130.8128;
const ratio = (semis: number) => Math.pow(2, semis / 12);
const fromSemis = (semis: number[]) => semis.map((s) => +(C3 * ratio(s)).toFixed(3));

export const TUNINGS: Record<string, Tuning> = {
  mystic: {
    name: 'mystic',
    label: 'Scriabin mystic chord + b9 + #5',
    // C3 F#3 Bb3 Db4 E4 G#4 A4 D5
    hz: fromSemis([0, 6, 10, 13, 16, 20, 21, 26]),
  },
  hitchcock: {
    name: 'hitchcock',
    label: 'minor/major seventh with tritone and b9',
    // C3 Eb3 F#3 G3 B3 Db4 Eb4 B4
    hz: fromSemis([0, 3, 6, 7, 11, 13, 15, 23]),
  },
  dim: {
    name: 'dim',
    label: 'diminished stack with a raised leading tone',
    // C3 Eb3 Gb3 A3 B3 D4 F4 Ab4
    hz: fromSemis([0, 3, 6, 9, 11, 14, 17, 20]),
  },
  pierce: {
    name: 'pierce',
    label: 'Bohlen-Pierce just subset (tritave-based, no octaves)',
    hz: [1, 9 / 7, 7 / 5, 5 / 3, 9 / 5, 15 / 7, 7 / 3, 3].map((r) => +(C3 * r).toFixed(3)),
  },
  concord: {
    name: 'concord',
    label: 'pentatonic (the control group)',
    // C3 D3 E3 G3 A3 C4 D4 E4
    hz: fromSemis([0, 2, 4, 7, 9, 12, 14, 16]),
  },
};

export const DEFAULT_TUNING = 'mystic';

export function resolveTuning(name: string | undefined): Tuning {
  return (name && TUNINGS[name]) || TUNINGS[DEFAULT_TUNING];
}

/** Colours shared by CSS and shaders (keep in sync with styles/base.css). */
export const PALETTE = {
  bg: '#0b0a0c',
  ink: '#e8e1d5',
  inkDim: '#a49d92',
  red: '#7a1e2a',
  redHot: '#c8323f',
  gold: '#c9a55a',
  iron: '#2a2a2e',
  bone: '#d9cfbf',
  brass: '#b08d4a',
} as const;

export const CONFIG = {
  seed: 23,
  ring: {
    center: { x: 0, y: 1.25, z: 0 },
    rx: 0.26,
    rz: 0.26,
  } satisfies RingSpec,
  hand: {
    /** the plane pointers are unprojected onto (world Z) */
    planeZ: 0,
    /** metres */
    radius: 0.07,
    /** how many simultaneous hands (multi-touch) */
    max: 5,
  },
  physics: {
    stepSeconds: 1 / 240,
    maxSubsteps: 12,
  },
  audio: {
    /**
     * Sound is wanted by default. Browsers still require one click/tap before an
     * AudioContext may run, so "enabled" only becomes "audible" after that gesture.
     * Flip this to false to ship muted-by-default; ?mute forces it off for a visit.
     */
    defaultEnabled: true,
    masterDb: -9,
    reverbSeconds: 7.5,
    wet: 0.72,
    /** slow per-tube random-walk detune, cents (physics owns the walk) */
    detuneCents: 9,
  },
  wave: {
    /** the de-rez ring travels outward from a strike at this speed */
    speedPxPerSec: 850,
    widthPx: 110,
    lifeSec: 1.6,
    maxActive: 16,
  },
  chord: {
    /** distinct tubes within windowSec to count as a chord */
    minTubes: 4,
    windowSec: 1.4,
    cooldownSec: 9,
  },
  logo: {
    text: 'discordant',
    tld: '.org',
  },
} as const;

export interface UrlParams {
  mute: boolean;
  lite: boolean | undefined;
  tuning: string | undefined;
  debug: string | undefined;
  seed: number | undefined;
}

export function readParams(): UrlParams {
  const q = new URLSearchParams(location.search);
  const has = (k: string) => q.has(k);
  const liteRaw = q.get('lite');
  return {
    mute: has('mute') || has('quiet'),
    lite: liteRaw === null ? undefined : liteRaw !== '0' && liteRaw !== 'false',
    tuning: q.get('tuning') ?? undefined,
    debug: q.get('debug') ?? undefined,
    seed: q.has('seed') ? Number(q.get('seed')) : undefined,
  };
}
